// extra-fx-analysis — analysis/edge effects
// Error Level Analysis (ELA), Canny edge detection, Flood-fill segmentation

const N = (v) => Number(v) || 0;

// ---- Error Level Analysis (ELA) ----
const errorLevelAnalysis = async (canvas, ctx, quality, scale) => {
    const w = canvas.width, h = canvas.height;
    const q = Math.max(10, Math.min(100, Math.floor(N(quality))));
    const sc = N(scale);
    // Re-encode at specified JPEG quality
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = w; tmpCanvas.height = h;
    const tmpCtx = tmpCanvas.getContext('2d');
    tmpCtx.drawImage(canvas, 0, 0);
    const dataUrl = tmpCanvas.toDataURL('image/jpeg', q / 100);
    const img2 = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = dataUrl;
    });
    const reCtx = document.createElement('canvas').getContext('2d');
    reCtx.canvas.width = w; reCtx.canvas.height = h;
    reCtx.drawImage(img2, 0, 0, w, h);
    const reData = reCtx.getImageData(0, 0, w, h).data;
    // Compute difference
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    for (let i = 0; i < data.length; i += 4) {
        const dr = Math.abs(data[i] - reData[i]);
        const dg = Math.abs(data[i+1] - reData[i+1]);
        const db = Math.abs(data[i+2] - reData[i+2]);
        const diff = (dr + dg + db) / 3;
        const amplified = Math.min(255, diff * (1 + sc));
        // Map: low diff = dark blue, high diff = bright white/red
        const t = amplified / 255;
        data[i] = Math.min(255, t * 255 * 2);
        data[i+1] = Math.min(255, t * 255 * 1.5 - 100);
        data[i+2] = Math.min(255, 100 - t * 100 + amplified * 0.5);
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Canny edge detection ----
const cannyEdge = async (canvas, ctx, low, high, blur) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const lo = N(low), hi = N(high);
    const blurAmt = N(blur);
    // Step 1: Grayscale + Gaussian blur
    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p++)
        gray[p] = 0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2];
    if (blurAmt > 0) {
        const r = Math.max(1, Math.floor(blurAmt));
        const kernel = gaussianKernel(r);
        const blurred = new Float32Array(w * h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            let sum = 0, wsum = 0;
            for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
                const xx = Math.max(0, Math.min(w-1, x+dx)), yy = Math.max(0, Math.min(h-1, y+dy));
                const wt = kernel[(dy+r)*(r*2+1)+(dx+r)];
                sum += gray[yy*w+xx] * wt; wsum += wt;
            }
            blurred[y*w+x] = sum / wsum;
        }
        gray.set(blurred);
    }
    // Step 2: Sobel gradient
    const mag = new Float32Array(w * h), dir = new Float32Array(w * h);
    for (let y = 1; y < h-1; y++) for (let x = 1; x < w-1; x++) {
        const p = y*w+x;
        const gx = -gray[p-w-1] - 2*gray[p-1] - gray[p+w-1] + gray[p-w+1] + 2*gray[p+1] + gray[p+w+1];
        const gy = -gray[p-w-1] - 2*gray[p-w] - gray[p-w+1] + gray[p+w-1] + 2*gray[p+w] + gray[p+w+1];
        mag[p] = Math.sqrt(gx*gx + gy*gy);
        dir[p] = Math.atan2(gy, gx);
    }
    // Step 3: Non-maximum suppression
    const nms = new Float32Array(w * h);
    for (let y = 1; y < h-1; y++) for (let x = 1; x < w-1; x++) {
        const p = y*w+x;
        const angle = dir[p] * 180 / Math.PI;
        const a = ((angle + 180) % 180);
        let n1 = 0, n2 = 0;
        if (a < 22.5 || a >= 157.5) { n1 = mag[p-1]; n2 = mag[p+1]; }
        else if (a < 67.5) { n1 = mag[p-w+1]; n2 = mag[p+w-1]; }
        else if (a < 112.5) { n1 = mag[p-w]; n2 = mag[p+w]; }
        else { n1 = mag[p-w-1]; n2 = mag[p+w+1]; }
        if (mag[p] >= n1 && mag[p] >= n2) nms[p] = mag[p];
    }
    // Step 4: Hysteresis threshold
    const edges = new Uint8Array(w * h);
    const stack = [];
    for (let p = 0; p < w*h; p++) if (nms[p] >= hi) { edges[p] = 1; stack.push(p); }
    while (stack.length) {
        const p = stack.pop();
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const x = (p % w) + dx, y = Math.floor(p / w) + dy;
            if (x < 0 || x >= w || y < 0 || y >= h) continue;
            const np = y * w + x;
            if (!edges[np] && nms[np] >= lo) { edges[np] = 1; stack.push(np); }
        }
    }
    // Render
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const v = edges[p] ? 255 : 0;
        data[i] = v; data[i+1] = v; data[i+2] = v; data[i+3] = 255;
    }
    ctx.putImageData(img, 0, 0);
};
function gaussianKernel(r) {
    const size = r * 2 + 1;
    const kernel = new Float32Array(size * size);
    const sigma = r / 2 || 0.5;
    let sum = 0;
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
        const v = Math.exp(-(dx*dx + dy*dy) / (2 * sigma * sigma));
        kernel[(dy+r)*size + (dx+r)] = v; sum += v;
    }
    for (let i = 0; i < kernel.length; i++) kernel[i] /= sum;
    return kernel;
}

// ---- Flood-fill segmentation ----
const floodSegment = async (canvas, ctx, tolerance, seeds) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const tol = N(tolerance);
    const numSeeds = Math.max(1, Math.floor(N(seeds)));
    const visited = new Uint8Array(w * h);
    const segmentId = new Int32Array(w * h).fill(-1);
    // Generate random seed points
    const seedPoints = [];
    for (let i = 0; i < numSeeds; i++) seedPoints.push({ x: Math.floor(Math.random()*w), y: Math.floor(Math.random()*h) });
    // Flood fill from each seed
    for (let s = 0; s < seedPoints.length; s++) {
        const { x: sx, y: sy } = seedPoints[s];
        const sp = sy * w + sx;
        if (visited[sp]) continue;
        const si = sp * 4;
        const sr = data[si], sg = data[si+1], sb = data[si+2];
        const stack = [sp];
        let sumR = 0, sumG = 0, sumB = 0, count = 0;
        const pixels = [];
        while (stack.length) {
            const p = stack.pop();
            if (visited[p]) continue;
            visited[p] = 1;
            segmentId[p] = s;
            const i = p * 4;
            pixels.push(p);
            sumR += data[i]; sumG += data[i+1]; sumB += data[i+2]; count++;
            const x = p % w, y = Math.floor(p / w);
            for (const [dx, dy] of [[1,0],[-1,0],[0,1],[0,-1]]) {
                const nx = x + dx, ny = y + dy;
                if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
                const np = ny * w + nx;
                if (visited[np]) continue;
                const ni = np * 4;
                const dr = Math.abs(data[ni] - sr), dg = Math.abs(data[ni+1] - sg), db = Math.abs(data[ni+2] - sb);
                if (dr <= tol && dg <= tol && db <= tol) stack.push(np);
            }
        }
        // Recolor segment with average color
        if (count > 0) {
            const ar = sumR / count, ag = sumG / count, ab = sumB / count;
            for (const p of pixels) {
                data[p*4] = ar; data[p*4+1] = ag; data[p*4+2] = ab;
            }
        }
    }
    ctx.putImageData(img, 0, 0);
};

const descriptors = [
    { name: 'anl-error-level', inputs: [
        { name: 'Quality', type: 'knob', min: 10, max: 100, step: 1, value: 90 },
        { name: 'Scale', type: 'knob', min: 0, max: 20, step: 1, value: 5 }], fn: errorLevelAnalysis },
    { name: 'anl-canny', inputs: [
        { name: 'Low', type: 'knob', min: 0, max: 100, step: 1, value: 30 },
        { name: 'High', type: 'knob', min: 0, max: 200, step: 1, value: 80 },
        { name: 'Blur', type: 'knob', min: 0, max: 5, step: 1, value: 1 }], fn: cannyEdge },
    { name: 'anl-flood-segment', inputs: [
        { name: 'Tolerance', type: 'knob', min: 1, max: 100, step: 1, value: 30 },
        { name: 'Seeds', type: 'knob', min: 5, max: 200, step: 1, value: 50 }], fn: floodSegment },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
